import { useEffect, useRef, useState } from 'react';
import { m, useInView } from 'framer-motion';
import { varAlpha } from 'minimal-shared/utils';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { styled, useTheme } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { CONFIG } from 'src/global-config';
import { useLocale } from 'src/locales';

import 'swiper/css';
import 'swiper/css/effect-fade';

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'warning', 'success'];

const SUMMARY = [
  { title: 'Years of experience', total: 12, icon: 'carbon:increase-level' },
  { title: 'Awards', total: 20, icon: 'carbon:trophy' },
  { title: 'Projects', total: 150, icon: 'carbon:data-vis-4' },
  { title: 'Happy clients', total: 32000, icon: 'carbon:user-certification' },
];

// ----------------------------------------------------------------------

export function BusinessSectorNeeds({ sx, ...other }) {
  const { t } = useLocale();
  const theme = useTheme();
  const direction = theme.direction;

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  // ✅ Image list with title & alt text
  const imageList = [
    {
      src: `${CONFIG.assetsDir}/assets/images/home/seo/Business-Solutions/shahen-construction-sector.webp`,
      alt: t('business-page-img2-title'),
      title: t('business-page-img2-meta-desk'),
    },
    {
      src: `${CONFIG.assetsDir}/assets/images/home/seo/Business-Solutions/shahen-fmcg-sector.webp`,
      alt: t('business-page-img3-title'),
      title: t('business-page-img3-meta-desk'),
    },
    {
      src: `${CONFIG.assetsDir}/assets/images/home/seo/Business-Solutions/shahen-government-sector.webp`,
      alt: t('business-page-img4-title'),
      title: t('business-page-img4-meta-desk'),
    },
    {
      src: `${CONFIG.assetsDir}/assets/images/home/seo/Business-Solutions/shahen-industrial-sector.webp`,
      alt: t('business-page-img5-title'),
      title: t('business-page-img5-meta-desk'),
    },
    {
      src: `${CONFIG.assetsDir}/assets/images/home/seo/Business-Solutions/shahen-retail-distribution-sector.webp`,
      alt: t('business-page-img6-title'),
      title: t('business-page-img6-meta-desk'),
    },
  ];

  // react-slick settings
  const settings = {
    dots: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 2500,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    fade: true,
    pauseOnHover: false,
    adaptiveHeight: true,
    rtl: direction === 'rtl',
  };

  const bulletPoints = [
    'Manufacturing_Industrial_Production',
    'Construction_Building_Materials',
    'Food_FMCG_Distribution',
    'Retail_Wholesale_Distribution',
    'Government_Public_Sector',
    'Logistics_Supply_Chain_Operations',
  ];

  return (
    <Box
      component={m.section}
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      sx={[
        {
          pt: { xs: 5, md: 10 },
          pb: { xs: 5, md: 10 },
          bgcolor: 'background.whitePaper',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Container>
        <Grid container spacing={3} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Image Section with auto-scroll */}
          <Grid size={{ xs: 12, md: 6, lg: 5 }}>
            <m.div
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Swiper
                key={direction}
                dir={direction}
                modules={[Autoplay, EffectFade]}
                effect="fade"
                autoplay={{ delay: 200, disableOnInteraction: false }}
                speed={800}
                loop
                style={{ width: '100%', maxWidth: 480 }}
              >
                {imageList.map((img, index) => (
                  <SwiperSlide key={index}>
                    <Box
                      component="img"
                      src={img.src}
                      alt={img.alt}
                      title={img.title}
                      sx={{
                        display: 'block',
                        width: '100%',
                        height: 480,
                        borderRadius: 2,
                        maxWidth: { xs: 300, sm: 400, md: 480 },
                        mx: 'auto',
                        maxHeight: { xs: 200, sm: 300, md: 480 },
                        objectFit: 'cover',
                      }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </m.div>
          </Grid>

          {/* Text Section */}
          <Grid sx={{ textAlign: { xs: 'center', md: 'left' } }} size={{ xs: 12, md: 6, lg: 6 }}>
            <m.div
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <Typography variant="h3" sx={{ fontSize: '2rem !important' }}>
                {t('Specialized_Expertise_for_Your_Sectors_Needs')}
              </Typography>

              <Typography sx={{ mt: 3, mb: 5, color: 'text.secondary' }}>
                {t('We_Understand_that_Every_Industry_has_Unique_Logistics_Challenges')}
              </Typography>

              {/* Bullet list */}
              <Box
                component="ul"
                sx={{
                  mb: 5,
                  gap: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  pl: 3,
                  color: 'text.secondary',
                  textAlign: 'left',
                  listStyleType: 'disc',
                  '& li::marker': {
                    color: 'green',
                    fontSize: '1.1rem',
                  },
                }}
              >
                {bulletPoints?.map((text, index) => (
                  <m.li
                    key={index}
                    initial={{ opacity: 0, x: 30 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                  >
                    {t(text)}
                  </m.li>
                ))}
              </Box>
            </m.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

const IconWrap = styled('div', {
  shouldForwardProp: (prop) => prop !== 'deg' && prop !== 'color',
})(({ color, deg, theme }) => ({
  width: 160,
  height: 160,
  margin: 'auto',
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  position: 'relative',
  justifyContent: 'center',
  color: theme.vars.palette[color].darker,
  border: `dashed 2px ${varAlpha(theme.vars.palette[color].mainChannel, 0.24)}`,
  '&:before': {
    zIndex: 8,
    content: '""',
    borderRadius: '50%',
    position: 'absolute',
    width: 'calc(100% - 48px)',
    height: 'calc(100% - 48px)',
    background: `conic-gradient(from ${deg}deg at 50% 50%, ${theme.vars.palette[color].main} 0deg, ${theme.vars.palette[color].light} 360deg)`,
  },
  '& svg': { zIndex: 9 },
}));
